import {Button, ButtonSpinner, Checkbox, Form, Input, Locale, Slideout} from '@local/ui/components'
import type {FormProps} from '@local/ui/components/form/Form'
import {cx} from '@local/utils/cx'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useTranslate} from '@local/hooks'
import PhoneNumberInput from '@local/ui/components/phoneNumberInput/PhoneNumberInput'
import {RetreatQuestionField} from './RetreatQuestionField'
import {normalizeRetreatFieldKey, type RetreatQuestion} from './types'

export interface PrivateRetreatSlideoutProps {
	listID: string
	successText: string
	successTitle: string
	questions: Array<RetreatQuestion>
	isOpen: boolean
	onClickClose: () => void
	/** When true (default), POST /api/newsletter/subscribe when subscribeMail is checked */
	newsletterOnSubmit?: boolean
}

function buildRetreatResponses (
	body: Record<string, unknown>,
	questions: Array<RetreatQuestion>,
): Record<string, string | Array<string>> {
	const out: Record<string, string | Array<string>> = {}
	for (const q of questions) {
		const key = normalizeRetreatFieldKey(q.fieldKey)
		if (!key) {
			continue
		}
		const raw = body[key]
		if (raw === undefined || raw === '' || raw === false) {
			continue
		}
		if (Array.isArray(raw)) {
			out[key] = raw.map(String)
		} else {
			out[key] = String(raw)
		}
	}
	return out
}

function validateQuestionInputs (
	body: Record<string, unknown>,
	questions: Array<RetreatQuestion>,
): Record<string, boolean> {
	const next: Record<string, boolean> = {}

	for (const q of questions) {
		const key = normalizeRetreatFieldKey(q.fieldKey)
		if (!key || q.required === false) {
			continue
		}

		let valid = true
		switch (q.inputType) {
			case 'radio':
			case 'radioWithDescription':
			case 'checkbox':
			case 'months': {
				const v = body[key]
				if (Array.isArray(v)) {
					valid = v.length > 0
				} else {
					valid = v !== undefined && v !== '' && v !== false
				}
				break
			}
			case 'dropdown': {
				const v = body[key]
				valid = v !== undefined && String(v).trim() !== ''
				break
			}
			case 'text':
			case 'textarea':
				valid = String(body[key] ?? '').trim() !== ''
				break
			default:
				valid = true
		}

		next[key] = !valid
	}

	return next
}

export const PrivateRetreatSlideout = forwardRef(
	(
		{
			isOpen = false,
			onClickClose,
			listID,
			successText,
			successTitle,
			questions,
			newsletterOnSubmit = true,
		}: PrivateRetreatSlideoutProps,
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const locale = useTranslate()

		const normalizedQuestions = useMemo(
			() =>
				questions.map((q) => ({
					...q,
					fieldKey: normalizeRetreatFieldKey(q.fieldKey),
				})),
			[questions],
		)

		const [inputs, setInputs] = useState({
			firstname: '',
			lastname: '',
			email: '',
			phoneNumber: '',
			countryCode: '',
			subscribeMail: false,
		})
		const [isLoading, setIsLoading] = useState<boolean>(false)
		const [isSuccess, setIsSuccess] = useState<boolean>(false)
		const [hasError, setHasError] = useState<boolean>(false)

		const formRef = useRef<HTMLFormElement>(null)
		const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({})

		const handleInput = (value: string, name: string) => {
			setInputs((prev) => ({...prev, [name]: value}))
		}

		const handleReset = () => {
			setInputs({
				firstname: '',
				lastname: '',
				email: '',
				phoneNumber: '',
				countryCode: '',
				subscribeMail: false,
			})
			setFieldErrors({})
		}

		const handleSubmit: FormProps['onSubmit'] = async ({body}) => {
			setIsLoading(true)
			try {
				setIsSuccess(false)
				setHasError(false)

				const bodyRecord = body as Record<string, unknown>

				const validationErrors = validateQuestionInputs(bodyRecord, normalizedQuestions)
				setFieldErrors(validationErrors)
				if (Object.values(validationErrors).some(Boolean)) {
					throw new Error('InvalidCheckbox')
				}

				if (!emailIsValid(String(bodyRecord.email ?? ''))) {
					throw new Error('InvalidEmail')
				}

				const retreatResponses = buildRetreatResponses(bodyRecord, normalizedQuestions)

				if (newsletterOnSubmit && bodyRecord.subscribeMail) {
					await fetch('/api/newsletter/subscribe/', {
						method: 'POST',
						body: JSON.stringify({email_address: bodyRecord.email, listID}),
					})
				}

				const res = await fetch('/api/contact/', {
					method: 'POST',
					body: JSON.stringify({
						listID,
						emailAddress: bodyRecord.email,
						phone: String(bodyRecord.countryCode ?? '') + String(bodyRecord.phoneNumber ?? ''),
						firstName: String(bodyRecord.firstname ?? ''),
						lastName: String(bodyRecord.lastname ?? ''),
						retreatResponses,
					}),
				})
				const result = await res.json()

				if (res.status === 200) {
					setIsSuccess(true)
					setIsLoading(false)
					return {status: 200}
				} else {
					setIsLoading(false)
					setHasError(true)
					return {errors: [result]}
				}
			} catch (error) {
				setIsLoading(false)
				setHasError(true)

				if (error instanceof Error && error.message === 'InvalidEmail') {
					return {errors: [{code: 400, message: 'Invalid email'}]}
				}
				if (error instanceof Error && error.message === 'InvalidCheckbox') {
					return {errors: [{code: 400, message: 'Invalid input'}]}
				}
				if (error instanceof Error && error.message === 'RequestFailed') {
					return {errors: [{code: 500, message: 'Request failed'}]}
				}
			}
		}

		useEffect(() => {
			if (isSuccess) {
				setTimeout(() => {
					onClickClose()
					setIsSuccess(false)
				}, 2000)
			}
		}, [isSuccess, onClickClose])

		return (
			<Slideout
				ref={ref}
				variant='right'
				isOpen={isOpen}
				onClickClose={onClickClose}
				className='bg-grey-4 text-white relative !px-0'>
				<div data-lenis-prevent className='overflow-y-scroll min-h-screen h-fit margin-px-1 flex flex-col scrollbar-hide'>
					<div className='sticky top-0 z-6 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
						<h2 className='w-fit uppercase'>{locale('Have a question?')}</h2>
						<Button
							small
							variant='icon'
							onClick={onClickClose}
							className='px-16 aspect-square'>
							<CloseSVG />
						</Button>
					</div>
					<Form
						ref={formRef}
						autoReset
						additionalReset={handleReset}
						onSubmit={handleSubmit}
						successMessage={<SuccessPanel title={successTitle} text={successText} />}
						errorClassName='bottom-8 text-center'
						successClassName='z-5 mt-0 -inset-1 bg-grey-4 w-[calc(100%+2px)]'
						className={cx(
							'flex flex-col h-fit w-full pt-40 gap-50',
							'[&_h3]:font-normal [&_h3]:text-18',
						)}>
						{normalizedQuestions.map((question) => (
							<RetreatQuestionField
								key={question.fieldKey}
								question={question}
								showError={Boolean(fieldErrors[question.fieldKey])} />
						))}

						<div className='grid grid-cols-2 gap-10'>
							<h3 className='col-span-2 mb-10'>
								<Locale>Your details so we can get in touch:</Locale>
							</h3>
							<Input
								required
								variant='dark'
								type='text'
								name='firstname'
								label={locale('Firstname')}
								value={inputs.firstname}
								onChange={e => handleInput(e.target.value, 'firstname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='text'
								name='lastname'
								label={locale('Lastname')}
								value={inputs.lastname}
								onChange={e => handleInput(e.target.value, 'lastname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='email'
								name='email'
								label='Email'
								value={inputs.email}
								onChange={e => handleInput(e.target.value, 'email')}
								onInvalid={() => setHasError(true)}
								pattern={EMAIL_PATTERN}
								hasError={hasError}
								className='h-48 col-span-2'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<PhoneNumberInput
								onChange={(value: string) => handleInput(value, 'phoneNumber')}
								onInvalid={() => setHasError(true)}
								hasError={hasError} />
							<Checkbox name='subscribeMail' theme='dark' className='col-span-2'>
								<span className='text-12 text-white'>Receive inspiration in your inbox</span>
							</Checkbox>
						</div>

						<div className='pb-25 w-full'>
							<ButtonSpinner
								type='submit'
								small
								loading={isLoading}
								variant='primary-transparent'
								className='mt-auto w-full'>
								<Locale>Send</Locale>
							</ButtonSpinner>
						</div>
					</Form>
				</div>
			</Slideout>
		)
	},
)

const SuccessPanel = ({title, text}: {title: string; text: string}) => (
	<div className='flex flex-col items-start pt-40 w-full h-fit gap-14 bg-grey-4 normal-case text-left sticky top-86'>
		<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
		<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{text}</p>
	</div>
)
